/**
 * Error Interceptor
 * Tüm gelen error response'ları intercept eder ve handle eder
 */

import { ApiErrorResponse, isApiErrorResponse } from '../../types/api.types';
import { HTTP_STATUS, AUTH_CONFIG } from '../../config/api.config';
import { store } from '../../../store';
import { logout, setToken, setUser } from '../../../store/slices/authSlice';

/**
 * Error interceptor interface
 */
export interface IErrorInterceptor {
  intercept(error: any): Promise<any>;
  shouldRetry(error: any): boolean;
  getPriority(): number;
}

/**
 * Base error interceptor
 */
export abstract class BaseErrorInterceptor implements IErrorInterceptor {
  abstract intercept(error: any): Promise<any>;
  
  shouldRetry(error: any): boolean {
    return false;
  }
  
  getPriority(): number {
    return 50;
  }
}

/**
 * Authentication error interceptor - 401 hatalarını handle eder
 */
export class AuthErrorInterceptor extends BaseErrorInterceptor {
  private isRefreshing = false;
  private refreshSubscribers: Array<(token: string) => void> = [];
  
  getPriority(): number {
    return 100; // High priority - ilk çalışsın
  }

  async intercept(error: any): Promise<any> {
    const originalRequest = error.config;
    
    // 401 Unauthorized hatası ve token refresh denenmemişse
    if (error.response?.status === HTTP_STATUS.UNAUTHORIZED && 
        !originalRequest._retry &&
        !originalRequest.skipAuthRefresh) {
      
      originalRequest._retry = true;
      
      try {
        // Token'ı refresh et
        const newToken = await this.refreshAccessToken();
        
        // Yeni token ile request'i tekrarla
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        
        // Bekleyen subscriber'ları notify et
        this.onRefreshSuccess(newToken);
        
        // Original request'i yeni token ile tekrarla
        return;
        
      } catch (refreshError) {
        // Refresh başarısızsa logout yap
        this.onRefreshFailed();
        return refreshError;
      }
    }
    
    throw error;
  }

  shouldRetry(error: any): boolean {
    return error.response?.status === HTTP_STATUS.UNAUTHORIZED && 
           !error.config?._retry &&
           !error.config?.skipAuthRefresh;
  }

  private async refreshAccessToken(): Promise<string> {
    if (this.isRefreshing) {
      // Zaten refresh işlemi devam ediyorsa, promise dön
      return new Promise((resolve) => {
        this.refreshSubscribers.push(resolve);
      });
    }
    
    this.isRefreshing = true;
    
    try {
      const refreshToken = localStorage.getItem(AUTH_CONFIG.STORAGE_KEYS.REFRESH_TOKEN);
      
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }
      
      // Refresh token endpoint'ini çağır
      // NOT: Bu kısmı kendi API'ne göre implement etmen gerekecek
      const response = await fetch('/auth/refresh', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken })
      });
      
      if (!response.ok) {
        throw new Error('Token refresh failed');
      }
      
      const data = await response.json();
      const newAccessToken = data.accessToken;
      
      localStorage.setItem(AUTH_CONFIG.STORAGE_KEYS.ACCESS_TOKEN, newAccessToken);
      
      // User bilgisini güncelle (eğer varsa)
      store.dispatch(setToken(newAccessToken));
      if (data.user) {
        store.dispatch(setUser(data.user));
      }

      // Yeni refresh token varsa onu da kaydet
      if (data.refreshToken) {
        localStorage.setItem(AUTH_CONFIG.STORAGE_KEYS.REFRESH_TOKEN, data.refreshToken);
      }
      
      this.isRefreshing = false;
      this.onRefreshSuccess(newAccessToken);
      
      return newAccessToken;
      
    } catch (error) {
      this.isRefreshing = false;
      this.onRefreshFailed();
      throw error;
    }
  }

  private onRefreshSuccess(token: string): void {
    for (const callback of this.refreshSubscribers) {
      callback(token);
    }
    this.refreshSubscribers = [];
  }

  private onRefreshFailed(): void {
    // Clear auth state
    store.dispatch(logout());
    
    // Clear tokens from storage
    localStorage.removeItem(AUTH_CONFIG.STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(AUTH_CONFIG.STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(AUTH_CONFIG.STORAGE_KEYS.USER_DATA);
    
    // Redirect to login
    if (globalThis.location.pathname !== '/login') {
      globalThis.location.href = '/login';
    }
  }
}

/**
 * Network error interceptor - Offline durumunu handle eder
 */
export class NetworkErrorInterceptor extends BaseErrorInterceptor {
  getPriority(): number {
    return 90;
  }

  async intercept(error: any): Promise<any> {
    // Network error (offline)
    if (!navigator.onLine) {
      console.warn('[API] Network is offline');
      
      // Event dispatch et (UI'da göstermek için)
      globalThis.dispatchEvent(new CustomEvent('network-offline'));
      
      // Retry queue'ya ekle
      this.addToRetryQueue(error.config);
    }
    
    // Timeout error
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      console.warn('[API] Request timeout');
      
      // Event dispatch et
      globalThis.dispatchEvent(new CustomEvent('request-timeout'));
    }
    
    throw error;
  }

  shouldRetry(error: any): boolean {
    return (!navigator.onLine || 
            error.code === 'ECONNABORTED' || 
            error.message?.includes('timeout')) &&
           error.config?._retryCount < (error.config?.retryCount || 3);
  }

  private addToRetryQueue(config: any): void {
    const queue = JSON.parse(localStorage.getItem('retry_queue') || '[]');
    queue.push({
      config,
      timestamp: Date.now(),
      retryCount: 0
    });
    localStorage.setItem('retry_queue', JSON.stringify(queue));
  }
}

/**
 * Server error interceptor - 5xx hatalarını handle eder
 */
export class ServerErrorInterceptor extends BaseErrorInterceptor {
  getPriority(): number {
    return 80;
  }

  async intercept(error: any): Promise<any> {
    // 5xx server errors
    if (error.response?.status >= 500) {
      console.error('[API] Server error:', error.response.status);
      
      // Error tracking service'e gönder (Sentry, LogRocket vb.)
      this.trackError(error);
      
      // User'a notification göster
      this.showErrorNotification(error);
      
      // Maintenance mode kontrolü
      if (error.response?.status === HTTP_STATUS.SERVICE_UNAVAILABLE) {
        globalThis.dispatchEvent(new CustomEvent('maintenance-mode'));
      }
    }
    
    throw error;
  }

  shouldRetry(error: any): boolean {
    // 5xx hatalarında retry yap (429 hariç - rate limiting)
    return error.response?.status >= 500 && 
           error.response?.status !== 429 &&
           error.config?._retryCount < (error.config?.retryCount || 3);
  }

  private trackError(error: any): void {
    // Error tracking implementation
    const sentry = (globalThis as any)['Sentry'];
    if (sentry) {
      sentry.captureException(error);
    }
  }

  private showErrorNotification(error: any): void {
    const event = new CustomEvent('show-toast', {
      detail: {
        type: 'error',
        title: 'Server Error',
        message: 'An unexpected error occurred. Please try again later.',
        duration: 5000
      }
    });
    globalThis.dispatchEvent(event);
  }
}

/**
 * Client error interceptor - 4xx hatalarını handle eder
 */
export class ClientErrorInterceptor extends BaseErrorInterceptor {
  getPriority(): number {
    return 70;
  }

  async intercept(error: any): Promise<any> {
    // 4xx client errors
    if (error.response?.status >= 400 && error.response?.status < 500) {
      
      // 403 Forbidden
      if (error.response?.status === HTTP_STATUS.FORBIDDEN) {
        console.warn('[API] Access forbidden');
        
        const event = new CustomEvent('show-toast', {
          detail: {
            type: 'warning',
            title: 'Access Denied',
            message: 'You do not have permission to perform this action.',
            duration: 5000
          }
        });
        globalThis.dispatchEvent(event);
      }
      
      // 404 Not Found
      else if (error.response?.status === HTTP_STATUS.NOT_FOUND) {
        console.warn('[API] Resource not found');
      }
      
      // 409 Conflict
      else if (error.response?.status === HTTP_STATUS.CONFLICT) {
        console.warn('[API] Resource conflict');
      }
      
      // 429 Rate Limit
      else if (error.response?.status === 429) {
        console.warn('[API] Rate limit exceeded');
        
        const retryAfter = error.response.headers['retry-after'];
        const event = new CustomEvent('show-toast', {
          detail: {
            type: 'warning',
            title: 'Rate Limit',
            message: `Too many requests. Please try again in ${retryAfter || 60} seconds.`,
            duration: 5000
          }
        });
        globalThis.dispatchEvent(event);
      }
      
      // Validation errors (422)
      else if (error.response?.status === 422) {
        console.warn('[API] Validation error');
        this.handleValidationErrors(error);
      }
    }
    
    throw error;
  }

  shouldRetry(error: any): boolean {
    // 4xx hatalarında genellikle retry yapma (429 hariç)
    return error.response?.status === 429 && 
           error.config?._retryCount < (error.config?.retryCount || 3);
  }

  private handleValidationErrors(error: any): void {
    const validationErrors = error.response?.data?.validationErrors;
    
    if (validationErrors && Array.isArray(validationErrors)) {
      const event = new CustomEvent('show-toast', {
        detail: {
          type: 'error',
          title: 'Validation Error',
          message: 'Please check your input fields.',
          errors: validationErrors,
          duration: 5000
        }
      });
      globalThis.dispatchEvent(event);
    }
  }
}

/**
 * Business logic error interceptor - API'nin kendi error formatını handle eder
 */
export class BusinessErrorInterceptor extends BaseErrorInterceptor {
  getPriority(): number {
    return 60;
  }

  async intercept(error: any): Promise<any> {
    // API'nin standart error formatına çevir
    const apiError = this.normalizeError(error);
    
    // Business logic error codes
    if (apiError.code) {
      switch (apiError.code) {
        case 'INSUFFICIENT_PERMISSIONS':
          this.handleInsufficientPermissions(apiError);
          break;
        case 'SESSION_EXPIRED':
          this.handleSessionExpired(apiError);
          break;
        case 'RESOURCE_LOCKED':
          this.handleResourceLocked(apiError);
          break;
        case 'DUPLICATE_ENTRY':
          this.handleDuplicateEntry(apiError);
          break;
        // Diğer business error codes...
      }
    }
    
    throw apiError;
  }

  private normalizeError(error: any): ApiErrorResponse {
    if (isApiErrorResponse(error.response?.data)) {
      return error.response.data;
    }
    
    if (error.response?.data) {
      return {
        message: error.response.data.message || 'An error occurred',
        status: error.response.status,
        error: error.response.data.error || 'Unknown Error',
        timestamp: new Date().toISOString(),
        path: error.config?.url,
        code: error.response.data.code,
        validationErrors: error.response.data.validationErrors
      };
    }
    
    return {
      message: error.message || 'Network Error',
      status: error.status || 0,
      error: 'Network Error',
      timestamp: new Date().toISOString(),
      path: error.config?.url
    };
  }

  private handleInsufficientPermissions(error: ApiErrorResponse): void {
    const event = new CustomEvent('show-toast', {
      detail: {
        type: 'warning',
        title: 'Insufficient Permissions',
        message: error.message,
        duration: 5000
      }
    });
    globalThis.dispatchEvent(event);
  }

  private handleSessionExpired(error: ApiErrorResponse): void {
    store.dispatch(logout());
    globalThis.location.href = '/login?session=expired';
  }

  private handleResourceLocked(error: ApiErrorResponse): void {
    const event = new CustomEvent('show-toast', {
      detail: {
        type: 'warning',
        title: 'Resource Locked',
        message: error.message,
        duration: 5000
      }
    });
    globalThis.dispatchEvent(event);
  }

  private handleDuplicateEntry(error: ApiErrorResponse): void {
    const event = new CustomEvent('show-toast', {
      detail: {
        type: 'warning',
        title: 'Duplicate Entry',
        message: error.message,
        duration: 5000
      }
    });
    globalThis.dispatchEvent(event);
  }
}

/**
 * Retry interceptor - Retry mekanizmasını yönetir
 */
export class RetryInterceptor extends BaseErrorInterceptor {
  getPriority(): number {
    return 10; // Low priority - son çalışsın
  }

  async intercept(error: any): Promise<any> {
    const config = error.config;
    
    if (!config) {
      throw error;
    }
    
    config._retryCount = config._retryCount || 0;
    
    // Check if we should retry
    if (this.shouldRetry(error)) {
      config._retryCount += 1;
      
      // Exponential backoff delay
      const delay = this.calculateRetryDelay(config._retryCount, config.retryDelay);
      
      console.log(`[API] Retrying request (${config._retryCount}/${config.retryCount || 3}) in ${delay}ms`);
      
      // Delay ile retry et
      await this.delay(delay);
      
      // Request'i tekrarla (bu kısım HttpClient tarafından handle edilecek)
      return 'retry';
    }
    
    throw error;
  }

  shouldRetry(error: any): boolean {
    const config = error.config;
    
    if (!config) return false;
    
    const maxRetries = config.retryCount || 3;
    const currentRetry = config._retryCount || 0;
    
    // Status code'a göre retry kararı
    const status = error.response?.status;
    const shouldRetryByStatus = 
      status === 429 || // Rate limit
      status >= 500 ||  // Server errors
      error.code === 'ECONNABORTED' || // Timeout
      error.message?.includes('timeout') ||
      !navigator.onLine; // Network offline
    
    return shouldRetryByStatus && currentRetry < maxRetries;
  }

  private calculateRetryDelay(retryCount: number, baseDelay: number = 1000): number {
    const delay = baseDelay || 1000;
    // Exponential backoff: 1s, 2s, 4s, 8s...
    return delay * Math.pow(2, retryCount - 1);
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

/**
 * Error Interceptor Manager - Tüm error interceptor'ları yönetir
 */
export class ErrorInterceptorManager {
  private interceptors: IErrorInterceptor[] = [];

  constructor() {
    // Default interceptor'ları ekle
    this.addInterceptor(new AuthErrorInterceptor());
    this.addInterceptor(new NetworkErrorInterceptor());
    this.addInterceptor(new ServerErrorInterceptor());
    this.addInterceptor(new ClientErrorInterceptor());
    this.addInterceptor(new BusinessErrorInterceptor());
    this.addInterceptor(new RetryInterceptor());
  }

  /**
   * Interceptor ekle
   */
  addInterceptor(interceptor: IErrorInterceptor): void {
    this.interceptors.push(interceptor);
    // Priority'ye göre sırala (yüksek priority önce)
    this.interceptors.sort((a, b) => b.getPriority() - a.getPriority());
  }

  /**
   * Interceptor kaldır
   */
  removeInterceptor(interceptor: IErrorInterceptor): void {
    const index = this.interceptors.indexOf(interceptor);
    if (index > -1) {
      this.interceptors.splice(index, 1);
    }
  }

  /**
   * Tüm interceptor'ları çalıştır
   */
  async intercept(error: any): Promise<any> {
    let processedError = error;

    for (const interceptor of this.interceptors) {
      try {
        processedError = await interceptor.intercept(processedError);
        
        // Eğer interceptor retry döndürdüyse
        if (processedError === 'retry') {
          return 'retry';
        }
        
      } catch (interceptorError) {
        console.error('[ErrorInterceptor] Interceptor failed:', interceptorError);
        // Bir interceptor fail etse bile diğerlerine devam et
        continue;
      }
    }

    return processedError;
  }

  /**
   * Retry gerekip gerekmediğini kontrol et
   */
  shouldRetry(error: any): boolean {
    return this.interceptors.some(interceptor => interceptor.shouldRetry(error));
  }

  /**
   * Tüm interceptor'ları temizle
   */
  clear(): void {
    this.interceptors = [];
  }

  /**
   * Interceptor sayısını getir
   */
  getCount(): number {
    return this.interceptors.length;
  }
}

// Singleton instance
export const errorInterceptorManager = new ErrorInterceptorManager();

// Default export
export default errorInterceptorManager;